import React from 'react';
import styled from 'styled-components';
import { utils } from '@makeship/core';
import StyledLink from '@makeship/core/lib/components/Link';
import { Element as ScrollElement } from 'react-scroll';

import ProductInfographic from '../ProductInfographic';
import { P2 } from '../../../Typography';
import { isHybridPreOrder, isHybridSoldOut, isReadyToShip, renderSupportLink } from '../commonFunctions';
import { getProductFromContext } from '../../../../context/product';
import { ProductTag } from '../../../../types/common';
import {
  hasProductTag,
  parseMetafields,
  getVariantShipDateInfo,
  getEffectiveShipDate,
} from '../../../../utils/product';
import config from '../../../../../config.json';
import { parseShopifyRichText } from '../../../../utils/metafields';
import { transformHtml } from '../../../../utils/htmr';
import { FaintCaption } from './sharedStyles';

const { routes } = config;

const HowItWorksWrapper = styled.div``;

const SectionHeading = styled(FaintCaption)`
  margin-top: 12px;
`;

const MoreInfoP2 = styled(P2)`
  margin-top: 12px;
`;

const HowItWorksToggle: React.FC = () => {
  const product = getProductFromContext();
  const isEvergreen = hasProductTag(product.tags, ProductTag.Evergreen);
  const isHybrid = hasProductTag(product.tags, ProductTag.Hybrid);
  const metafields = parseMetafields(product.metafields);

  const shouldDisplayHybridShippingCopy = isHybrid && isReadyToShip(product);

  // Get variant ship date information
  const { hasDifferentShipDates, allVariantsReadyToShip } = getVariantShipDateInfo(product);

  // Get effective ship date (variant-specific or product-level)
  // Variant dates take precedence over product delivery date
  const effectiveShipDate = getEffectiveShipDate(product, undefined, metafields.delivery);

  const renderShippingInfo = () => {
    // Show order confirmation message for sold-out hybrid products
    if (isHybridSoldOut(product)) {
      return (
        <P2>
          <strong>Shipping times vary based on when you ordered.</strong> Check your order confirmation email for the
          most up to date shipment information.
        </P2>
      );
    }

    if (isHybrid && hasDifferentShipDates && !allVariantsReadyToShip) {
      return (
        <>
          <P2>Shipping dates vary by size. Select a size to view its estimated ship date.</P2>
          <br />
          <P2>
            <strong>
              📌 If you previously ordered this product, check your order confirmation email for your estimated ship
              date.
            </strong>
          </P2>
        </>
      );
    }

    // Custom hybrid shipping info from metafield
    if (metafields.hybridShippingInfo) {
      return (
        <>
          {transformHtml(parseShopifyRichText(metafields.hybridShippingInfo))}
          {isHybrid && (
            <>
              <br />
              <P2>
                <strong>
                  📌 If you previously ordered this product, check your order confirmation email for your estimated ship
                  date.
                </strong>
              </P2>
            </>
          )}
        </>
      );
    }

    // Ready-to-ship hybrid
    if (shouldDisplayHybridShippingCopy) {
      return (
        <>
          <P2>This product is ready-to-ship. Each order will be shipped 2-4 days after purchase.</P2>
          <FaintCaption> Note: Shipping address changes are not possible for ready-to-ship products.</FaintCaption>
          <br />
          <P2>
            <strong>
              📌 If you previously ordered this product, check your order confirmation email for your estimated ship
              date.
            </strong>
          </P2>
        </>
      );
    }

    // Pre-order hybrid
    if (isHybridPreOrder(product)) {
      return (
        <>
          <P2>
            This product is a pre-order. Orders are estimated to start shipping on{' '}
            {effectiveShipDate && utils.getDate(effectiveShipDate)}.
          </P2>
          <br />
          <P2>
            <strong>
              📌 If you previously ordered this product, check your order confirmation email for your estimated ship
              date.
            </strong>
          </P2>
        </>
      );
    }

    return <P2>This product is ready-to-ship. Each order will be shipped 2-4 days after purchase.</P2>;
  };

  return isEvergreen || isHybrid ? (
    <>
      <HowItWorksWrapper>
        <FaintCaption>Shipping</FaintCaption>
        <ScrollElement name="shipping-info" />
        {renderShippingInfo()}
        <SectionHeading>Delivery</SectionHeading>
        <P2>
          Once shipped, estimated delivery is 7-15 business days. *Delivery to some areas may take 10-30 business days.
        </P2>

        <SectionHeading>Returns and Refunds</SectionHeading>
        <P2>
          All sales are final and non-refundable. For damaged products, please visit{' '}
          <StyledLink.Primary target="_blank" href={routes.helpCenter}>
            Makeship Help Center.
          </StyledLink.Primary>
        </P2>
      </HowItWorksWrapper>
    </>
  ) : (
    <HowItWorksWrapper>
      <ProductInfographic />
      <SectionHeading>Returns and Refunds</SectionHeading>
      <P2>
        All sales are final and non-refundable. If a Campaign does not achieve its funding goal, all orders will be
        refunded within 4-7 business days.
      </P2>
      <MoreInfoP2>Want more information? {renderSupportLink(product)}</MoreInfoP2>
    </HowItWorksWrapper>
  );
};

export default HowItWorksToggle;
